import React from 'react';
import { Calendar, Clock, MapPin, ChefHat, Beer, User, ArrowRight, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

interface DinnerRecommendation {
  id: string;
  type: 'sent' | 'received';
  status: 'accepted' | 'pending' | 'rejected';
  date: string;
  time?: string;
  message: string;
  created_at: string;
  with_user: {
    id: string;
    full_name: string;
    profile_picture?: string;
  };
  venue?: {
    id: string;
    name: string;
    address?: string;
    cuisine_type?: string;
    bar_type?: string;
    pictures?: {
      exterior?: string[];
      interior?: string[];
      gallery?: string[];
    };
  };
}

interface DinnerRecommendationsListProps {
  recommendations: DinnerRecommendation[];
}

const DinnerRecommendationsList: React.FC<DinnerRecommendationsListProps> = ({ recommendations }) => {
  const { t } = useLanguage();
  
  if (recommendations.length === 0) {
    return (
      <div className="text-center py-6 bg-gray-50 rounded-lg">
        <p className="text-gray-500">{t('myDinners.noRecommendations')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {recommendations.map((recommendation) => {
        const isVenueBar = recommendation.venue?.bar_type !== undefined;
        const isAccepted = recommendation.status === 'accepted';
        const isSent = recommendation.type === 'sent';
        
        return (
          <div 
            key={recommendation.id}
            className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 hover:shadow-md transition"
          >
            <div className="flex flex-col md:flex-row">
              {/* Venue Image */}
              <div className="md:w-1/4 h-40 md:h-auto relative">
                {recommendation.venue?.pictures?.exterior?.[0] || recommendation.venue?.pictures?.gallery?.[0] ? (
                  <img 
                    src={recommendation.venue.pictures.exterior?.[0] || recommendation.venue.pictures.gallery?.[0]}
                    alt={recommendation.venue.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.error(`Failed to load image: ${recommendation.venue?.pictures?.exterior?.[0] || recommendation.venue?.pictures?.gallery?.[0]}`);
                      (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2000&auto=format&fit=crop';
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    {isVenueBar ? (
                      <Beer className="h-12 w-12 text-gray-400" />
                    ) : (
                      <ChefHat className="h-12 w-12 text-gray-400" />
                    )}
                  </div>
                )}
                
                {/* Status badge */}
                <div className={`absolute top-2 right-2 px-3 py-1 rounded-full text-xs font-medium ${
                  isSent 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-green-100 text-green-700'
                }`}>
                  {isSent ? t('myDinners.recommendedByMe') : t('myDinners.acceptedFromOthers')}
                </div>
              </div>
              
              {/* Content */}
              <div className="p-4 md:p-6 flex-1">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {recommendation.venue?.name || t('myDinners.dinnerInvitation')}
                    </h3>
                    {recommendation.venue && (
                      <div className="flex items-center text-gray-600 text-sm mt-1">
                        {isVenueBar ? (
                          <Beer className="h-4 w-4 mr-1" />
                        ) : (
                          <ChefHat className="h-4 w-4 mr-1" />
                        )}
                        <span>
                          {isVenueBar 
                            ? recommendation.venue.bar_type 
                            : recommendation.venue.cuisine_type}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {recommendation.with_user.profile_picture ? (
                      <img 
                        src={recommendation.with_user.profile_picture}
                        alt={recommendation.with_user.full_name}
                        className="w-8 h-8 rounded-full object-cover"
                        onError={(e) => {
                          console.error(`Failed to load profile image: ${recommendation.with_user.profile_picture}`);
                          (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=150&h=150&auto=format&fit=crop';
                        }}
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="h-4 w-4 text-gray-400" />
                      </div>
                    )}
                    <div className="flex items-center text-sm">
                      {isSent ? (
                        <>
                          <span className="text-gray-600">To:</span>
                          <ArrowRight className="h-3 w-3 mx-1 text-gray-400" />
                        </>
                      ) : (
                        <>
                          <span className="text-gray-600">From:</span>
                          <ArrowLeft className="h-3 w-3 mx-1 text-gray-400" />
                        </>
                      )}
                      <Link 
                        to={`/profile/${recommendation.with_user.id}`}
                        className="font-medium text-blue-600 hover:text-blue-800"
                      >
                        {recommendation.with_user.full_name}
                      </Link>
                    </div>
                  </div>
                </div>
                
                {/* Message */}
                <p className="text-gray-600 mb-4 line-clamp-2">
                  "{recommendation.message}"
                </p>
                
                {/* Details */}
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                  {recommendation.date && (
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{new Date(recommendation.date).toLocaleDateString()}</span>
                    </div>
                  )}
                  
                  {recommendation.time && (
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>{recommendation.time.slice(0, 5)}</span>
                    </div>
                  )}
                  
                  {recommendation.venue?.address && (
                    <div className="flex items-center col-span-2">
                      <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                      <span className="truncate">{recommendation.venue.address}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center col-span-2 text-xs text-gray-500 mt-2">
                    <span>{t('myDinners.recommendedOn')} {new Date(recommendation.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DinnerRecommendationsList;